import React from 'react';
import { motion } from 'framer-motion';
import { Clock3, Building2, ArrowUpRight, ShieldCheck, CheckCircle2, FileClock } from 'lucide-react';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const draftNames = {
  organization: 'Organization details',
  core_assessment: 'Core assessment',
  technical_controls: 'Technical controls',
  documentation_readiness: 'Documentation readiness',
};

const getDraftRoute = (draft) => ({
  organization: draft.org_id ? `/organization?orgId=${draft.org_id}` : '/organization',
  core_assessment: `/core-assessment/${draft.org_id || ''}`,
  technical_controls: `/technical-controls/${draft.org_id || ''}`,
  documentation_readiness: `/documentation-readiness/${draft.org_id || ''}`,
}[draft.draft_type] || '/organization');

const getAssessmentRoute = (assessment) => {
  if (assessment.resume_url) return assessment.resume_url;
  if (assessment.status === 'completed') return `/assessment-summary/${assessment.org_id}`;
  if (assessment.progress <= 25) return `/core-assessment/${assessment.org_id}`;
  if (assessment.progress <= 50) return `/technical-controls/${assessment.org_id}`;
  if (assessment.progress <= 75) return `/documentation-readiness/${assessment.org_id}`;
  return `/controls-assessment/${assessment.org_id}`;
};

const EmptyState = ({ navigate }) => (
  <div className="flex min-h-56 flex-col items-center justify-center px-5 text-center" data-testid="assessments-empty-state">
    <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl border border-white/10 bg-white/5"><Building2 className="h-6 w-6 text-slate-500" /></div>
    <p className="font-medium text-slate-200">No assessments yet</p>
    <p className="mt-2 text-sm text-slate-500">Start your first ISO 27001 maturity assessment.</p>
    <Button onClick={() => navigate('/organization?new=true')} className="mt-5 bg-emerald-500 text-slate-950 hover:bg-emerald-400" data-testid="start-first-assessment-button"><ShieldCheck className="mr-2 h-4 w-4" />Start assessment</Button>
  </div>
);

const AssessmentsList = ({ stats, drafts, navigate }) => {
  const assessments = stats?.organization_assessments || [];

  return (
    <motion.section
      className="dashboard-glass rounded-xl p-5 sm:p-6"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: 0.65 }}
      data-testid="assessments-workspace-panel"
    >
      <div className="mb-5 flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
        <div><p className="dashboard-eyebrow">Assessment workspace</p><h2 className="mt-2 font-heading text-2xl font-medium text-white">Your assessments</h2></div>
        <p className="text-xs text-slate-500" data-testid="assessments-record-count">{assessments.length} records · {drafts.length} drafts</p>
      </div>

      <Tabs defaultValue="assessments" className="w-full" data-testid="assessments-tabs">
        <TabsList className="h-11 w-full justify-start rounded-lg border border-white/10 bg-slate-950/40 p-1 sm:w-auto">
          <TabsTrigger value="assessments" className="flex-1 text-slate-400 data-[state=active]:bg-white/10 data-[state=active]:text-white sm:flex-none" data-testid="assessments-tab-trigger">Assessments <span className="ml-2 rounded bg-white/10 px-1.5 py-0.5 text-[10px]">{assessments.length}</span></TabsTrigger>
          <TabsTrigger value="drafts" className="flex-1 text-slate-400 data-[state=active]:bg-white/10 data-[state=active]:text-white sm:flex-none" data-testid="drafts-tab-trigger">Drafts <span className="ml-2 rounded bg-white/10 px-1.5 py-0.5 text-[10px]">{drafts.length}</span></TabsTrigger>
        </TabsList>

        <TabsContent value="assessments" className="mt-5" data-testid="assessments-tab-content">
          {assessments.length ? (
            <div className="divide-y divide-white/[0.07]" data-testid="assessments-list">
              {assessments.map((assessment, index) => (
                <motion.button
                  key={assessment.org_id}
                  type="button"
                  className="group flex w-full flex-col gap-4 px-2 py-5 text-left transition-colors hover:bg-white/[0.025] sm:flex-row sm:items-center sm:justify-between"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  onClick={() => navigate(getAssessmentRoute(assessment))}
                  data-testid={`assessment-row-${assessment.org_id}`}
                >
                  <div className="flex min-w-0 items-center gap-4">
                    <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border ${assessment.status === 'completed' ? 'border-emerald-400/25 bg-emerald-400/10' : 'border-amber-400/25 bg-amber-400/10'}`}>
                      <Building2 className={`h-5 w-5 ${assessment.status === 'completed' ? 'text-emerald-300' : 'text-amber-300'}`} />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate font-medium text-slate-100">{assessment.org_name || 'Unnamed organization'}</p>
                      <p className="mt-1 truncate text-xs text-slate-500">{assessment.industry || 'Industry not provided'}{assessment.created_at ? ` · ${new Date(assessment.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-4 pl-[60px] sm:pl-0">
                    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${assessment.status === 'completed' ? 'border-emerald-400/20 bg-emerald-400/10 text-emerald-200' : 'border-amber-400/20 bg-amber-400/10 text-amber-200'}`}>
                      {assessment.status === 'completed' ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Clock3 className="h-3.5 w-3.5" />}
                      {assessment.status === 'completed' ? 'Completed' : `${assessment.current_stage || `${assessment.progress || 0}% complete`}`}
                    </span>
                    <ArrowUpRight className="h-4 w-4 text-slate-600 transition-colors group-hover:text-white" />
                  </div>
                </motion.button>
              ))}
            </div>
          ) : <EmptyState navigate={navigate} />}
        </TabsContent>

        <TabsContent value="drafts" className="mt-5" data-testid="drafts-tab-content">
          {drafts.length ? (
            <div className="divide-y divide-white/[0.07]" data-testid="drafts-list">
              {drafts.map((draft, index) => (
                <button key={draft.draft_id || index} type="button" onClick={() => navigate(getDraftRoute(draft))} className="group flex w-full flex-col gap-4 px-2 py-5 text-left transition-colors hover:bg-white/[0.025] sm:flex-row sm:items-center sm:justify-between" data-testid={`draft-row-${draft.draft_id || index}`}>
                  <div className="flex min-w-0 items-center gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-amber-400/25 bg-amber-400/10"><FileClock className="h-5 w-5 text-amber-300" /></div>
                    <div className="min-w-0"><p className="truncate font-medium text-slate-100">{draftNames[draft.draft_type] || draft.draft_type}</p><p className="mt-1 truncate text-xs text-slate-500">{draft.data?.org_name || 'Organization not named'} · Saved {new Date(draft.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p></div>
                  </div>
                  <div className="flex items-center justify-between gap-4 pl-[60px] sm:pl-0"><span className="rounded-full border border-amber-400/20 bg-amber-400/10 px-2.5 py-1 text-xs text-amber-200">Continue draft</span><ArrowUpRight className="h-4 w-4 text-slate-600 transition-colors group-hover:text-white" /></div>
                </button>
              ))}
            </div>
          ) : (
            <div className="flex min-h-48 flex-col items-center justify-center text-center" data-testid="drafts-empty-state"><FileClock className="mb-3 h-8 w-8 text-slate-600" /><p className="text-sm text-slate-400">You have no saved drafts.</p></div>
          )}
        </TabsContent>
      </Tabs>
    </motion.section>
  );
};

export default AssessmentsList;